import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "../ui/sonner";
import { Button } from "../ui/button";
import { ReasonModal } from "./ReasonModal";
import { formatDateTime } from "../../lib/dateUtils";

const STATUS_FILTERS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

export const PhotoReviewTab = () => {
  const [list, setList] = useState(null);
  const [status, setStatus] = useState("pending");
  const [rejectTarget, setRejectTarget] = useState(null);

  const load = () => {
    api.get("/admin/photo-review", { params: { status } }).then(({ data }) => setList(data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (id) => {
    try {
      await api.post(`/admin/photo-review/${id}/approve`);
      toast.success("Photo approved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't approve photo");
    }
  };

  const reject = async (reason) => {
    try {
      await api.post(`/admin/photo-review/${rejectTarget}/reject`, { reason });
      toast.success("Photo rejected");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reject photo");
    }
  };

  return (
    <div data-testid="photo-review-tab">
      <p className="text-sm font-medium text-midnight-black/60 mb-6 max-w-lg">
        Every Stranger must submit a clear photo of themselves at signup. Approve or reject each submission below — rejected accounts are blocked from booking until they upload a new photo.
      </p>
      <div className="flex flex-wrap gap-3 mb-6">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={`photo-review-status-filter-${f.value}`}
            onClick={() => setStatus(f.value)}
            className={`text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full border-2 transition-colors ${status === f.value ? "bg-stranger-orange text-clean-white border-stranger-orange" : "border-soft-gray text-midnight-black/70"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {list === null ? (
        <p className="font-medium text-midnight-black/60">Loading...</p>
      ) : list.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-photo-review">Nothing here right now.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="photo-review-list">
          {list.map((p) => (
            <div key={p.id} className="border border-soft-gray rounded-2xl p-5" data-testid={`photo-review-row-${p.id}`}>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/profile-photo/${p.id}?v=${p.photo_submitted_at || ""}`}
                alt={`${p.first_name} ${p.last_name}`}
                className="w-full h-48 object-cover rounded-xl mb-3 bg-warm-white"
                data-testid={`photo-review-image-${p.id}`}
              />
              <p className="font-extrabold text-sm uppercase">{p.first_name} {p.last_name}</p>
              <p className="text-xs font-medium text-midnight-black/60 mb-1">{p.email}</p>
              <p className="text-[10px] font-bold uppercase tracking-wide text-midnight-black/40 mb-3">Submitted {formatDateTime(p.photo_submitted_at)}</p>
              {p.photo_rejection_reason && (
                <p className="text-xs font-bold text-red-600 mb-3">Rejected: {p.photo_rejection_reason}</p>
              )}
              {p.photo_status === "pending" && (
                <div className="flex gap-2">
                  <Button data-testid={`approve-photo-${p.id}`} onClick={() => approve(p.id)} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full text-xs flex-1">
                    APPROVE
                  </Button>
                  <Button data-testid={`reject-photo-${p.id}`} onClick={() => setRejectTarget(p.id)} variant="outline" className="rounded-full font-bold text-xs text-red-600 flex-1">
                    REJECT
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ReasonModal
        open={!!rejectTarget}
        onOpenChange={(v) => !v && setRejectTarget(null)}
        title="Reject this photo"
        description="This reason will be shared with the Stranger so they can resubmit."
        confirmLabel="REJECT"
        onConfirm={reject}
      />
    </div>
  );
};
